/**
 * The triage queue.
 *
 * An operator opens this to answer one question: what does nobody have yet?
 * So it opens on exactly that — open alerts no person has acknowledged, worst
 * and newest first — with the acknowledged ones one press away rather than
 * mixed in. Severity narrows it, the filter box finds a camera or a cause, and
 * a row opens the asset.
 *
 * 🔴 Acknowledged is not resolved. An acknowledgement says a person has this
 * and nothing about whether the fault is over; the engine still resolves on
 * evidence. That is why acknowledged alerts get their own queue rather than
 * disappearing: a screen that hid them would be rendering "someone has it" as
 * "it is handled", which is a product decision nobody has taken.
 */

import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ApiError, api, type AlertRow, type Fleet } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  FilterChip,
  Pill,
  Section,
  Toolbar,
  Tooltip,
  severityState,
  type ColumnDef,
} from './components/index.ts';

type Queue = 'open' | 'acked' | 'all';
type Severity = AlertRow['severity'];

const SEVERITIES: readonly Severity[] = ['critical', 'warning', 'info'];
const SEVERITY_RANK: Record<Severity, number> = { critical: 2, warning: 1, info: 0 };

/** An age an operator can read, with the timestamp one hover away. */
function age(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

interface Row {
  alert: AlertRow;
  asset: string;
  site: string | null;
}

export function AlertsView({
  alerts,
  fleet,
  onChanged,
  onOpenAsset,
}: {
  alerts: AlertRow[];
  fleet: Fleet;
  onChanged: () => void;
  onOpenAsset: (assetId: string) => void;
}): React.JSX.Element {
  const [queue, setQueue] = useState<Queue>('open');
  /** The alert being resolved by hand, if any. */
  const [resolving, setResolving] = useState<Row | null>(null);
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [query, setQuery] = useState('');

  const rows = useMemo<Row[]>(() => {
    const siteName = new Map(fleet.sites.map((s) => [s.id, s.name]));
    const assets = new Map(fleet.assets.map((a) => [a.id, a]));
    return alerts.map((alert) => {
      const asset = assets.get(alert.asset_id);
      return {
        alert,
        asset: asset?.code ?? asset?.name ?? alert.asset_id.slice(0, 8),
        site: asset?.site_id != null ? (siteName.get(asset.site_id) ?? null) : null,
      };
    });
  }, [alerts, fleet.assets, fleet.sites]);

  const inQueue = (r: Row, q: Queue): boolean =>
    q === 'all' || (q === 'open' ? r.alert.ack_at === null : r.alert.ack_at !== null);

  const queueCounts: Record<Queue, number> = {
    open: rows.filter((r) => inQueue(r, 'open')).length,
    acked: rows.filter((r) => inQueue(r, 'acked')).length,
    all: rows.length,
  };

  // Severity counts are within the queue on screen, so a chip never offers more than pressing it shows.
  const queued = rows.filter((r) => inQueue(r, queue));
  const severityCounts = Object.fromEntries(
    SEVERITIES.map((s) => [s, queued.filter((r) => r.alert.severity === s).length]),
  ) as Record<Severity, number>;

  const needle = query.trim().toLowerCase();
  const shown = queued.filter((r) => {
    if (severity !== null && r.alert.severity !== severity) return false;
    if (needle === '') return true;
    return [r.asset, r.site, r.alert.cause_label, r.alert.message].some(
      (v) => typeof v === 'string' && v.toLowerCase().includes(needle),
    );
  });

  const columns = useMemo<ColumnDef<Row, unknown>[]>(
    () => [
      {
        id: 'severity',
        header: 'Severity',
        accessorFn: (r) => SEVERITY_RANK[r.alert.severity],
        cell: ({ row }) => (
          <Pill state={severityState(row.original.alert.severity)}>{row.original.alert.severity}</Pill>
        ),
        size: 96,
      },
      {
        id: 'asset',
        header: 'Asset',
        accessorFn: (r) => r.asset.toLowerCase(),
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">{row.original.asset}</div>
            {row.original.site !== null && <div className="small muted">{row.original.site}</div>}
          </div>
        ),
      },
      {
        id: 'cause',
        header: 'Cause',
        accessorFn: (r) => r.alert.cause_label,
        cell: ({ row }) => (
          <div>
            <div>{row.original.alert.cause_label}</div>
            <div className="small muted">{row.original.alert.message}</div>
          </div>
        ),
      },
      {
        id: 'action',
        header: 'What to do',
        accessorFn: (r) => r.alert.operator_action ?? '',
        enableSorting: false,
        cell: ({ row }) => <span className="small muted">{row.original.alert.operator_action ?? '—'}</span>,
      },
      {
        id: 'raised',
        header: 'Age',
        accessorFn: (r) => r.alert.raised_at,
        cell: ({ row }) => (
          <Tooltip content={new Date(row.original.alert.raised_at).toLocaleString()}>
            <span className="small muted">{age(row.original.alert.raised_at)}</span>
          </Tooltip>
        ),
        size: 72,
      },
      {
        id: 'ack',
        header: 'Who has it',
        accessorFn: (r) => r.alert.ack_at ?? '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="stack-tight" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <AckCell alert={row.original.alert} onChanged={onChanged} />
            {/*
              Resolve is beside acknowledge and says less: acknowledging is "I
              have this", resolving is "I have this and I do not want to hear
              about it again today". Neither claims the fault is fixed, which is
              why the drawer asks for a reason and shows the window.
            */}
            <Button size="sm" variant="ghost" onClick={() => setResolving(row.original)}>
              Resolve…
            </Button>
          </div>
        ),
        size: 180,
      },
    ],
    [onChanged],
  );

  if (alerts.length === 0) {
    /*
      An empty alerts screen is ambiguous and the ambiguity matters: "nothing
      is wrong" and "nothing is being checked" look identical. Fleet counts say
      which, so this points at them rather than leaving a blank card that reads
      as either good news or a broken page.
    */
    return (
      <Empty
        tone="good"
        title="No open alerts"
        body="Nothing is currently raised. The Fleet counters say how many assets have a current reading at all."
      />
    );
  }

  const queueLabel: Record<Queue, string> = {
    open: 'Needs someone',
    acked: 'Acknowledged',
    all: 'All open',
  };

  return (
    <>
      <Toolbar>
        <div className="chip-group" role="group" aria-label="Queue">
          {(['open', 'acked', 'all'] as const).map((q) => (
            <FilterChip key={q} active={queue === q} onClick={() => setQueue(q)}>
              {queueLabel[q]} <span className="chip-count">{queueCounts[q]}</span>
            </FilterChip>
          ))}
        </div>
        <span className="toolbar-sep" aria-hidden="true" />
        <div className="chip-group" role="group" aria-label="Severity">
          {SEVERITIES.map((s) => (
            <FilterChip
              key={s}
              active={severity === s}
              onClick={() => setSeverity((current) => (current === s ? null : s))}
            >
              {s} <span className="chip-count">{severityCounts[s]}</span>
            </FilterChip>
          ))}
        </div>
        <label className="search-field">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Filter by asset, site or cause"
            aria-label="Filter alerts"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </Toolbar>

      {resolving !== null && (
        <ResolveDrawer
          row={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            onChanged();
          }}
        />
      )}

      <Section flush>
        <DataTable
          data={shown}
          columns={columns}
          getRowId={(r) => r.alert.id}
          initialSort={[
            { id: 'severity', desc: true },
            { id: 'raised', desc: true },
          ]}
          onRowClick={(r) => onOpenAsset(r.alert.asset_id)}
          rowClass={(r) =>
            r.alert.severity === 'critical' && r.alert.ack_at === null ? 'row-fault' : undefined
          }
          minWidth={880}
          caption="Open alerts"
          empty={
            queue === 'open' && severity === null && needle === '' ? (
              <Empty
                tone="good"
                title="Every open alert has someone on it"
                body={`${queueCounts.acked} acknowledged and still open. Acknowledged is not resolved.`}
              />
            ) : (
              <Empty title="Nothing matches" />
            )
          }
        />
      </Section>
    </>
  );
}

/**
 * The acknowledge control. Holds state because the ack is optimistic-free —
 * the button disables, the request goes, and the row is refreshed from what
 * the server said. The one thing worse than an alert that looks unhandled is
 * one that looks handled and is not.
 *
 * Clicks stop here: the row behind it opens the asset, and pressing
 * Acknowledge must not also navigate away from the queue.
 */
export function AckCell({ alert, onChanged }: { alert: AlertRow; onChanged: () => void }): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const toggle = async (): Promise<void> => {
    setBusy(true);
    setFailed(null);
    try {
      if (alert.ack_at === null) await api.post(`/api/alerts/${alert.id}/ack`);
      else await api.del(`/api/alerts/${alert.id}/ack`);
      onChanged();
    } catch (err) {
      setFailed(err instanceof ApiError ? err.message : 'could not reach the server');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="stack-tight"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {alert.ack_at === null ? (
        <Button size="sm" busy={busy} onClick={() => void toggle()}>
          Acknowledge
        </Button>
      ) : (
        <>
          <span className="small muted">
            {alert.ack_by_name ?? 'someone'} · {age(alert.ack_at)} ago
          </span>
          <Button size="sm" variant="ghost" busy={busy} onClick={() => void toggle()}>
            Withdraw
          </Button>
        </>
      )}
      {failed !== null && <span className="caveat">{failed}</span>}
    </div>
  );
}

const WINDOWS = [
  { hours: 4, label: '4 hours' },
  { hours: 24, label: 'a day' },
  { hours: 72, label: '3 days' },
  { hours: 168, label: 'a week' },
];

/**
 * Resolving one alert by hand.
 *
 * 🔴 The drawer says what a resolve IS, because the word means something else
 * everywhere else: it does not mark the fault fixed, it holds the cause for a
 * window and then lets it back. Decision 5, 2026-09-16.
 */
function ResolveDrawer({
  row,
  onClose,
  onResolved,
}: {
  row: Row;
  onClose: () => void;
  onResolved: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Drawer
      open
      onClose={onClose}
      title={`Resolve ${row.asset}`}
      subtitle={`${row.alert.cause_label} · raised ${age(row.alert.raised_at)} ago`}
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        <Banner tone="info" title="A resolve is not a fix">
          The check is still failing. This records your judgement and stops this cause being raised on{' '}
          {row.asset} for the window you choose. If it is still failing when that ends, the alert comes back —
          and if the device recovers first, the alert closes on its own.
        </Banner>

        <Field id="resolve-reason" label="Why" hint="Read back by whoever sees this alert return.">
          <input
            id="resolve-reason"
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="going to site tomorrow with a card"
          />
        </Field>

        <Field id="resolve-hours" label="Hold it for">
          <select id="resolve-hours" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="row">
          <Button
            variant="primary"
            busy={busy}
            disabled={reason.trim() === ''}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api.post(`/api/alerts/${row.alert.id}/resolve`, { reason, hours });
                onResolved();
              } catch (err) {
                setError(err instanceof ApiError ? err.message : 'could not resolve');
              } finally {
                setBusy(false);
              }
            }}
          >
            Resolve for {WINDOWS.find((w) => w.hours === hours)?.label ?? `${hours}h`}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
